import type React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
	CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";

import type { AssetPart } from "@/pages/asset-details/models/AssetPart";
import { PartsCatalogFreshness } from "@/shared/catalogs/components/CatalogFreshnessControls";
import { PartsTable } from "./parts-table";
import { upsertAssetParts } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";

interface Props {
	searchAssetParts: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	AssetId: number | null;
	isLoading: boolean;
	listAllParts: AssetPart[];
	// Lets the reload control hand the fresh inventory back to whoever owns the list.
	onCatalogReloaded?: (parts: any[]) => void;
}

export const PartsPicker: React.FC<Props> = ({
	searchAssetParts,
	show,
	onHide,
	AssetId,
	isLoading,
	listAllParts,
	onCatalogReloaded,
}) => {
	const {
		state: { listAssetParts },
	} = useAssetDetailsContext();

	const [selectedParts, setSelectedParts] = useState<AssetPart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);
	const allParts = useMemo(() => Object.values(listAllParts), [listAllParts]);

	const currentAssetParts = useMemo(() => {
		return listAssetParts.filter((part) => {
			return Number(part.AssetId) === Number(AssetId);
		});
	}, [listAssetParts, AssetId]);

	useEffect(() => {
		if (show) {
			setSelectedParts(currentAssetParts);
		}
	}, [show, currentAssetParts]);

	const handleSelectionChanged = (newSelectedParts: AssetPart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const onCancelHandler = useCallback(() => onHide(false), [onHide]);

	const handleSave = async () => {
		setLoadingSave(true);

		try {
			const partNamesToLink = selectedParts
				.map((part) => part.Name)
				.filter((name): name is string => name !== null && name !== undefined);

			const res = await upsertAssetParts(
				AssetId as number,
				partNamesToLink,
				null,
			);

			if (!res.ok) {
				setError(res.message);
				setLoadingSave(false);
				return;
			}

			const addedCount = res.data?.tables?.[0]?.rows?.[0]?.AddedCount ?? 0;
			const removedCount = res.data?.tables?.[0]?.rows?.[0]?.RemovedCount ?? 0;

			if (addedCount > 0 || removedCount > 0) {
				const messages = [];
				if (addedCount > 0) messages.push(`${addedCount} part(s) added`);
				if (removedCount > 0) messages.push(`${removedCount} part(s) removed`);
				setSuccessMessage(messages.join(", "));
				onHide(true);
			} else {
				onHide(false);
			}
		} catch (err: any) {
			setError(err?.message || "Failed to update parts");
		} finally {
			setLoadingSave(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" sx={{ margin: 0, fontWeight: 600 }}>
						PARTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<PartsTable
						allParts={allParts}
						assetParts={currentAssetParts}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
					<PartsCatalogFreshness
						onReloaded={onCatalogReloaded}
						onError={setError}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={onCancelHandler}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						startIcon={loadingSave && <CircularProgress size="1rem" />}
						disabled={loadingSave}
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
